import {
  ClipboardEvent,
  KeyboardEvent,
  FocusEvent,
  FormEvent,
  useRef,
  useState,
} from "react";
import { DocumentNode, useMutation } from "@apollo/client";
import { stripHtml } from "string-strip-html";

import { CLIP_COMMENT_FIELDS } from "MVVM/GraphQL/clipComments";
import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";

const MAX_COMMENT_SIZE = 600;
const ELLIPSIS = "...";
const GENERIC_ERROR = "Couldn't post comment — try again";

// The server caps text at MAX_COMMENT_SIZE, so the ellipsis has to fit inside
// the cap rather than be appended past it.
const truncate = (text: string) =>
  text.length > MAX_COMMENT_SIZE
    ? `${text.substring(0, MAX_COMMENT_SIZE - ELLIPSIS.length)}${ELLIPSIS}`
    : text;

interface IUseCommentFormArgs {
  mutation: DocumentNode;
  buildVariables: (
    text: string,
    gifUrl: string | null,
  ) => Record<string, unknown>;
  getCacheTarget: (commentCreate: any) => { __typename: string; id: string };
  setPortalOpen?: (value: boolean) => void;
}

export const useCommentForm = ({
  mutation,
  buildVariables,
  getCacheTarget,
  setPortalOpen,
}: IUseCommentFormArgs) => {
  const placeholder = "Add a comment...";
  const [, setComment] = useState(placeholder);
  const [disabled, setDisabled] = useState(true);
  const [gifUrl, setGifUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLElement | null>(null);
  const [createComment, { loading }] = useMutation(mutation);
  const { allstarUser } = useUser();

  const handleFocus = (e: FocusEvent<HTMLDivElement>) => {
    if (!allstarUser.loggedIn && setPortalOpen) {
      e.target.blur();

      return setPortalOpen(true);
    }

    setComment("");
    setDisabled(false);
  };

  const submit = (comment: string, gif: string | null) => {
    createComment({
      variables: buildVariables(comment, gif),
      onCompleted: (data) => {
        const commentCreate = data?.commentCreate;
        const serverError = commentCreate?.errors?.[0]?.message;

        if (serverError || !commentCreate?.comment) {
          setErrorMessage(serverError || GENERIC_ERROR);
          return;
        }

        setErrorMessage(null);
        setComment("");
        if (inputRef.current) inputRef.current.textContent = "";
        setGifUrl(null);
        setDisabled(true);

        analytics.track("Comment Created", {
          platform: "web",
          hasGif: Boolean(gif),
        });
      },
      onError: () => setErrorMessage(GENERIC_ERROR),
      update(cache, { data }) {
        const commentCreate = data?.commentCreate;
        if (!commentCreate?.comment) return;

        const newCommentRef = cache.writeFragment({
          data: commentCreate.comment,
          fragment: CLIP_COMMENT_FIELDS,
        });

        const cacheId = cache.identify(getCacheTarget(commentCreate));
        if (!cacheId) return;

        cache.modify({
          id: cacheId,
          fields: {
            comments(existingComments = {}) {
              const existingNodes = existingComments.nodes || [];
              return {
                ...existingComments,
                nodes: [newCommentRef, ...existingNodes],
                pageInfo: {
                  ...existingComments.pageInfo,
                  totalCount: (existingComments.pageInfo?.totalCount || 0) + 1,
                },
              };
            },
          },
        });
      },
    });
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    const text = e.clipboardData.getData("text/plain");
    const selection = window.getSelection();
    if (!selection) return;

    const range = selection.getRangeAt(0);
    const textNode = document.createTextNode(text);
    range.deleteContents();
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);

    const el = e.currentTarget;
    const newContent = el.textContent || "";
    setComment(newContent);
    setErrorMessage(null);
    setDisabled((!newContent.trim() || newContent === placeholder) && !gifUrl);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      validateComment();
    }
  };

  const handleInput = (e: FormEvent<HTMLDivElement>) => {
    const textContent = e.currentTarget.textContent || "";
    setComment(textContent);
    setErrorMessage(null);

    const hasContent = textContent.trim() && textContent !== placeholder;
    setDisabled(!hasContent && !gifUrl);
  };

  const handleClick = () => validateComment();

  const validateComment = () => {
    const actualContent = inputRef.current?.textContent || "";
    const trimmedComment = actualContent.trim();

    if (
      !allstarUser.loggedIn ||
      (!trimmedComment && !gifUrl) ||
      trimmedComment === placeholder ||
      loading
    ) {
      return;
    }

    const strippedComment = trimmedComment
      ? stripHtml(trimmedComment).result
      : "";

    submit(truncate(strippedComment), gifUrl);
  };

  const handleGifSelect = (url: string) => {
    if (!allstarUser.loggedIn) {
      if (setPortalOpen) setPortalOpen(true);
      return;
    }

    setGifUrl(url);
    setDisabled(false);
  };

  const handleGifRemove = () => {
    setGifUrl(null);
    const hasText = Boolean(inputRef.current?.textContent?.trim());
    setDisabled(!hasText);
  };

  const handleEmojiSelect = (emoji: string) => {
    if (!inputRef.current) return;

    const currentContent = inputRef.current.textContent || "";
    const isPlaceholder =
      currentContent === placeholder || currentContent === "";
    const newContent = isPlaceholder ? emoji : currentContent + emoji;

    inputRef.current.textContent = newContent;
    setComment(newContent);
    setErrorMessage(null);
    setDisabled(false);
    inputRef.current.focus();

    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.selectNodeContents(inputRef.current);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  return {
    loading,
    inputRef,
    disabled,
    gifUrl,
    errorMessage,
    handleFocus,
    handleKeyDown,
    handleInput,
    handlePaste,
    handleClick,
    handleEmojiSelect,
    handleGifSelect,
    handleGifRemove,
  };
};
